/**
 * Alert - Web implementation
 * Custom styled modal dialog for web platform
 */
import React, { useState, useCallback } from 'react';
import { createRoot, Root } from 'react-dom/client';

type AlertButton = {
  text?: string;
  onPress?: () => void;
  style?: 'default' | 'cancel' | 'destructive';
};

type AlertOptions = {
  cancelable?: boolean;
  onDismiss?: () => void;
};

// Styles as plain objects for inline styling
const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    animation: 'fadeIn 0.2s ease-out',
  },
  dialog: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    minWidth: 270,
    maxWidth: 400,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
    overflow: 'hidden',
    animation: 'scaleIn 0.2s ease-out',
  },
  content: {
    padding: '20px 16px',
    textAlign: 'center' as const,
  },
  title: {
    fontSize: 17,
    fontWeight: 600,
    color: '#000000',
    margin: 0,
    marginBottom: 4,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  message: {
    fontSize: 13,
    color: '#666666',
    margin: 0,
    marginTop: 4,
    lineHeight: 1.4,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    whiteSpace: 'pre-wrap' as const,
  },
  buttonContainer: {
    display: 'flex',
    borderTop: '1px solid #E0E0E0',
  },
  buttonContainerVertical: {
    flexDirection: 'column' as const,
  },
  buttonContainerHorizontal: {
    flexDirection: 'row' as const,
  },
  button: {
    flex: 1,
    padding: '12px 16px',
    fontSize: 17,
    fontWeight: 400,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    transition: 'background-color 0.15s ease',
  },
  buttonDefault: {
    color: '#007AFF',
    fontWeight: 600,
  },
  buttonCancel: {
    color: '#007AFF',
    fontWeight: 400,
  },
  buttonDestructive: {
    color: '#FF3B30',
    fontWeight: 400,
  },
  buttonDivider: {
    width: 1,
    backgroundColor: '#E0E0E0',
  },
  buttonDividerHorizontal: {
    borderTop: '1px solid #E0E0E0',
  },
};

// CSS animations
const styleSheet = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes scaleIn {
    from { transform: scale(0.95); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  .alert-button:hover {
    background-color: rgba(0, 0, 0, 0.05) !important;
  }
  .alert-button:active {
    background-color: rgba(0, 0, 0, 0.1) !important;
  }
`;

interface AlertDialogProps {
  title: string;
  message?: string;
  buttons: AlertButton[];
  onClose: () => void;
  cancelable?: boolean;
  onDismiss?: () => void;
}

const AlertDialog: React.FC<AlertDialogProps> = ({
  title,
  message,
  buttons,
  onClose,
  cancelable = true,
  onDismiss,
}) => {
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && cancelable) {
        onDismiss?.();
        onClose();
      }
    },
    [cancelable, onDismiss, onClose],
  );

  const handleButtonClick = useCallback(
    (button: AlertButton) => {
      button.onPress?.();
      onClose();
    },
    [onClose],
  );

  const getButtonStyle = (button: AlertButton, isLast: boolean) => {
    const baseStyle = { ...styles.button };

    if (button.style === 'destructive') {
      Object.assign(baseStyle, styles.buttonDestructive);
    } else if (button.style === 'cancel') {
      Object.assign(baseStyle, styles.buttonCancel);
    } else {
      Object.assign(baseStyle, styles.buttonDefault);
    }

    return baseStyle;
  };

  const useVerticalLayout = buttons.length > 2;

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <style>{styleSheet}</style>
      <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div style={styles.content}>
          <h2 style={styles.title}>{title}</h2>
          {message && <p style={styles.message}>{message}</p>}
        </div>
        <div
          style={{
            ...styles.buttonContainer,
            ...(useVerticalLayout
              ? styles.buttonContainerVertical
              : styles.buttonContainerHorizontal),
          }}
        >
          {buttons.map((button, index) => (
            <React.Fragment key={index}>
              {index > 0 && !useVerticalLayout && <div style={styles.buttonDivider} />}
              {index > 0 && useVerticalLayout && <div style={styles.buttonDividerHorizontal} />}
              <button
                className="alert-button"
                style={getButtonStyle(button, index === buttons.length - 1)}
                onClick={() => handleButtonClick(button)}
              >
                {button.text || 'OK'}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

// Global container and root for alerts
let alertContainer: HTMLDivElement | null = null;
let alertRoot: Root | null = null;

const getAlertContainer = (): HTMLDivElement => {
  if (!alertContainer) {
    alertContainer = document.createElement('div');
    alertContainer.id = 'alert-root';
    document.body.appendChild(alertContainer);
  }
  return alertContainer;
};

const getAlertRoot = (): Root => {
  if (!alertRoot) {
    alertRoot = createRoot(getAlertContainer());
  }
  return alertRoot;
};

const alert = (
  title: string,
  message?: string,
  buttons?: AlertButton[],
  options?: AlertOptions,
): void => {
  const resolvedButtons = buttons && buttons.length > 0 ? buttons : [{ text: 'OK' }];

  const handleClose = () => {
    getAlertRoot().render(null);
  };

  getAlertRoot().render(
    <AlertDialog
      title={title}
      message={message}
      buttons={resolvedButtons}
      onClose={handleClose}
      cancelable={options?.cancelable}
      onDismiss={options?.onDismiss}
    />,
  );
};

export const Alert = {
  alert,
};

export default Alert;
